/**
 * Lab cases (NEW-WF-12, module id `lab`, routes under `/v1/lab`). Library:
 * lab-referral-rx.md (LR-L1..L5, LR-L8), imaging-docs MM-11.
 *
 * Labs and services are practice-wide; cases belong to one office. Due dates
 * are office-local `YYYY-MM-DD`; instants are ISO 8601 strings. Cases made in
 * Velora are kept in Velora (origin 'velora'); they are never written to the
 * PMS. Lab cases synced from a PMS are listed read-only.
 */
import { z } from 'zod';
import { DoInPmsSchema } from './pms-todo.js';

const Id = z.uuid();
const LocalDate = z.iso.date();
const Instant = z.string().min(1);

export const LAB_PERMISSIONS = {
  /** See labs, cases and the board. */
  view: 'schedule.view',
  /** Create, send, receive, check and cancel cases (desk, assistants, clinicians). */
  edit: 'task.create',
  /** Add and edit labs and their services. */
  setup: 'settings.manage',
} as const;

export const LabCaseStatusSchema = z.enum(['created', 'sent', 'received', 'checked', 'cancelled']);
export type LabCaseStatus = z.infer<typeof LabCaseStatusSchema>;
export const LabTimingSchema = z.enum(['on_time', 'after_ready_by', 'late']);
export type LabTimingValue = z.infer<typeof LabTimingSchema>;

export const LabServiceSchema = z.object({
  id: Id,
  labId: Id,
  name: z.string(),
  turnaroundDays: z.number().int(),
  feeCents: z.number().int().nullable(),
  active: z.boolean(),
});
export type LabService = z.infer<typeof LabServiceSchema>;

export const LabSchema = z.object({
  id: Id,
  name: z.string(),
  contactName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  turnaroundDays: z.number().int().nullable(),
  readyBufferDays: z.number().int(),
  /** The lab's closed days (holidays); due dates skip them and weekends. */
  closedDays: z.array(LocalDate),
  active: z.boolean(),
  /**
   * The PMS this lab comes from (null = made in Velora). Its name and contact
   * details are the PMS's and read-only; ready buffer, closed days, services
   * and active stay Velora settings.
   */
  pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']).nullable(),
  services: z.array(LabServiceSchema),
});
export type Lab = z.infer<typeof LabSchema>;
export const LabListSchema = z.object({ labs: z.array(LabSchema), canSetup: z.boolean() });
export type LabList = z.infer<typeof LabListSchema>;

export const LabBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  contactName: z.string().trim().max(120).nullable().optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  readyBufferDays: z.number().int().min(0).max(10).optional(),
  closedDays: z.array(LocalDate).max(120).optional(),
  active: z.boolean().optional(),
});
export const LabServiceBodySchema = z.object({
  name: z.string().trim().min(1).max(120),
  turnaroundDays: z.number().int().min(0).max(60),
  feeCents: z.number().int().min(0).max(10_000_000).optional(),
  active: z.boolean().optional(),
});

export const LabCaseEventSchema = z.object({
  kind: z.enum([
    'created',
    'edited',
    'sent',
    'received',
    'checked',
    'cancelled',
    'linked',
    'tracked',
  ]),
  at: Instant,
  byName: z.string(),
  detail: z.record(z.string(), z.unknown()),
});

export const LabCaseSchema = z.object({
  id: Id,
  locationId: Id,
  patientId: Id,
  /** "Last, First" (null when names are hidden). */
  patientName: z.string().nullable(),
  caseNumber: z.string().nullable(),
  labId: Id.nullable(),
  labName: z.string().nullable(),
  serviceId: Id.nullable(),
  serviceName: z.string().nullable(),
  providerId: Id.nullable(),
  providerName: z.string().nullable(),
  appointmentId: Id.nullable(),
  /** Seat appointment start, if attached. */
  seatAt: Instant.nullable(),
  procedureIds: z.array(Id),
  procedures: z.array(z.object({ id: Id, code: z.string(), tooth: z.string().nullable() })),
  teeth: z.string().nullable(),
  shade: z.string().nullable(),
  instructions: z.string().nullable(),
  status: LabCaseStatusSchema,
  sentAt: Instant.nullable(),
  dueDate: LocalDate.nullable(),
  dueOverride: z.boolean(),
  readyByDate: LocalDate.nullable(),
  receivedAt: Instant.nullable(),
  receivedByName: z.string().nullable(),
  tracking: z.string().nullable(),
  timing: LabTimingSchema.nullable(),
  timingText: z.string(),
  /** 'velora' cases can be edited; 'pms' cases are read-only copies. */
  origin: z.enum(['velora', 'pms']),
  /** The PMS a read-only case comes from (null for Velora cases). */
  pmsSystem: z.enum(['opendental', 'dentrix', 'eaglesoft']).nullable(),
  createdAt: Instant,
  /**
   * A PMS case staff marked received in Velora before the PMS says so: kept
   * in Velora only (no connector command for lab cases). status then shows
   * 'received'.
   */
  receivedInVelora: z.boolean().optional(),
  /** Tracking number recorded in Velora for a PMS case (kept in Velora only). */
  trackingInVelora: z.boolean().optional(),
  /** What to do in the PMS so it matches Velora ("Do this in Dentrix"). */
  doInPms: DoInPmsSchema.nullable().optional(),
  events: z.array(LabCaseEventSchema).optional(),
});
export type LabCase = z.infer<typeof LabCaseSchema>;

export const LabCaseListQuerySchema = z.object({
  status: z.enum(['open', 'all', 'created', 'sent', 'received', 'checked', 'cancelled']).optional(),
  lane: z.enum(['late', 'due_soon', 'all']).optional(),
  labId: Id.optional(),
  providerId: Id.optional(),
  patientId: Id.optional(),
  appointmentId: Id.optional(),
});
export const LabCaseListSchema = z.object({
  cases: z.array(LabCaseSchema),
  counts: z.object({ open: z.number().int(), late: z.number().int(), dueSoon: z.number().int() }),
  canEdit: z.boolean(),
});
export type LabCaseList = z.infer<typeof LabCaseListSchema>;

export const CreateLabCaseBodySchema = z.object({
  patientId: Id,
  appointmentId: Id.nullable().optional(),
  labId: Id,
  serviceId: Id.nullable().optional(),
  providerId: Id.nullable().optional(),
  procedureIds: z.array(Id).max(16).optional(),
  teeth: z.string().trim().max(60).nullable().optional(),
  shade: z.string().trim().max(40).nullable().optional(),
  instructions: z.string().trim().max(2000).nullable().optional(),
  /** Overrides the computed due date. */
  dueDate: LocalDate.nullable().optional(),
  /** Send now (sets sent and computes the due date). */
  send: z.boolean().optional(),
});
export type CreateLabCaseBody = z.infer<typeof CreateLabCaseBodySchema>;

export const UpdateLabCaseBodySchema = z.object({
  appointmentId: Id.nullable().optional(),
  serviceId: Id.nullable().optional(),
  providerId: Id.nullable().optional(),
  teeth: z.string().trim().max(60).nullable().optional(),
  shade: z.string().trim().max(40).nullable().optional(),
  instructions: z.string().trim().max(2000).nullable().optional(),
  dueDate: LocalDate.nullable().optional(),
  tracking: z.string().trim().max(80).nullable().optional(),
});

export const SendLabCaseBodySchema = z.object({
  /** Office-local send date; default today. */
  sentDate: LocalDate.optional(),
  tracking: z.string().trim().max(80).nullable().optional(),
});
export const CancelLabCaseBodySchema = z.object({ reason: z.string().trim().min(1).max(500) });

/** Receive by scanning slip QR codes (case numbers) or picking cases. */
export const ReceiveBodySchema = z.object({
  caseNumbers: z.array(z.string().trim().min(1).max(40)).max(100).optional(),
  caseIds: z.array(Id).max(100).optional(),
});
export const ReceiveResultSchema = z.object({
  received: z.array(LabCaseSchema),
  notFound: z.array(z.string()),
  alreadyBack: z.array(z.string()),
});
export type ReceiveResult = z.infer<typeof ReceiveResultSchema>;

/** Records a tracking number; for a PMS case it is kept in Velora only. */
export const TrackLabCaseBodySchema = z.object({
  tracking: z.string().trim().max(80).nullable(),
});
export type TrackLabCaseBody = z.infer<typeof TrackLabCaseBodySchema>;

/** A patient's open procedures, to pick for a lab case or a referral. */
export const PatientProcedureSchema = z.object({
  id: Id,
  code: z.string(),
  description: z.string().nullable(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  /** OD ProcStat: 1 TP, 2 C, 3 EC, 4 EO, 5 R, 8 TPi. */
  status: z.number().int(),
  appointmentId: Id.nullable(),
  appointmentAt: Instant.nullable(),
  isProsthesis: z.boolean(),
  /** Quadrant or arch ("UR") for area procedures such as scaling and root planing. */
  area: z.string().nullable().optional(),
});
export type PatientProcedure = z.infer<typeof PatientProcedureSchema>;
export const PatientProceduresQuerySchema = z.object({
  /** Also list this procedure whatever its status (the one a lab case starts from). */
  include: Id.optional(),
});
export const PatientProcedureListSchema = z.object({
  procedures: z.array(PatientProcedureSchema),
  appointments: z.array(z.object({ id: Id, startsAt: Instant, label: z.string() })),
});
export type PatientProcedureList = z.infer<typeof PatientProcedureListSchema>;
